import { Link } from 'react-router-dom';

export default function ForParents() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 text-9xl">💙</div>
          <div className="absolute bottom-10 right-10 text-9xl">🏠</div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full text-sm font-medium mb-6">
              <span className="mr-2">👨‍👩‍👧‍👦</span>
              For Families
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Turn Screen Time Into <span className="text-accent-300">Speech Practice</span>
            </h1>
            
            <p className="text-xl text-primary-100 mb-8 leading-relaxed">
              You know your child better than anyone. SpeechFormation gives you a fun, 
              pressure-free way to encourage speech at home—no therapy degree required.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 hover:bg-accent-600 text-white font-bold text-lg rounded-xl transition-all shadow-lg"
              >
                Start Free Today
              </Link>
              <Link
                to="/games"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold text-lg rounded-xl border-2 border-white/30 transition-all"
              >
                Explore Games
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* We Get It Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              We get it. Therapy fatigue is real.
            </h2>
          </div>
          
          <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-3xl p-8 sm:p-12 border border-primary-100">
            <div className="space-y-6 text-lg text-gray-700">
              <p>
                After school, after homework, after therapy sessions... the last thing your child wants is 
                <em> more</em> practice that feels like work.
              </p>
              <p>
                And the last thing <em>you</em> need is another battle. You're already doing so much.
              </p>
              <p>
                <strong>What if practice didn't feel like practice?</strong>
              </p>
              <p>
                What if your child <em>asked</em> to play? What if speech practice was just... playing a game?
              </p>
              <p className="text-primary-600 font-semibold">
                That's exactly what SpeechFormation is designed to do.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Helps Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How SpeechFormation Helps at Home
            </h2>
            <p className="text-xl text-gray-600">
              Speech practice disguised as genuine fun
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-primary-100 rounded-2xl flex items-center justify-center mb-5">
                <span className="text-3xl">🎮</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Voice-Controlled Games
              </h3>
              <p className="text-gray-600">
                Your child speaks to play. Pop balloons by saying "POP!" Help Pip the cat by asking for 
                snacks. Speech becomes the game controller.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-accent-100 rounded-2xl flex items-center justify-center mb-5">
                <span className="text-3xl">🧠</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Built on Real Science
              </h3>
              <p className="text-gray-600">
                Developed with clinical psychologists using ABA principles and speech therapy best 
                practices—but your child just sees a fun game.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center mb-5">
                <span className="text-3xl">📊</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Progress You Can See
              </h3>
              <p className="text-gray-600">
                Track play time, levels completed, and engagement. Share reports with your child's 
                therapist to show what's working.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tips for Parents Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Tips for Using SpeechFormation at Home
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-primary-500 text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Keep It Fun, Not Forced</h3>
                <p className="text-gray-600">
                  Let your child lead. If they want to stop, that's okay. Short, positive sessions 
                  are better than long, frustrating ones.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-primary-500 text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Play Together</h3>
                <p className="text-gray-600">
                  Sit with your child and celebrate their wins. Your enthusiasm is contagious and 
                  makes the experience more rewarding.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-primary-500 text-white rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Consistency Over Intensity</h3>
                <p className="text-gray-600">
                  10 minutes a day beats an hour once a week. Regular, brief play sessions build 
                  habits and confidence over time.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-primary-500 text-white rounded-full flex items-center justify-center font-bold">
                4
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">Share With Your Therapist</h3>
                <p className="text-gray-600">
                  Let your child's speech therapist know what games they love. They can suggest 
                  which activities align with current therapy goals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alongside Therapy Section */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Works Alongside Professional Therapy
              </h2>
              <p className="text-gray-300 text-lg mb-6">
                SpeechFormation isn't meant to replace your child's therapist—it's designed to 
                complement the work they're already doing.
              </p>
              
              <div className="space-y-4">
                {[
                  'Reinforce therapy goals through play at home',
                  'Give your child extra practice without the pressure',
                  'Build confidence between therapy sessions',
                  'Keep speech practice fun so your child stays engaged',
                ].map((point, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-gray-300">{point}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/10 rounded-3xl p-8 backdrop-blur border border-white/10">
              <div className="text-center">
                <div className="text-6xl mb-4">💡</div>
                <h3 className="text-xl font-bold mb-4">Important Note</h3>
                <p className="text-gray-300">
                  SpeechFormation is an entertainment platform designed to make speech practice 
                  enjoyable. It's not a medical device or therapy replacement. Always follow your 
                  healthcare provider's recommendations for your child's specific needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-3xl p-8 sm:p-12 text-center">
            <div className="text-5xl mb-6">💬</div>
            <blockquote className="text-xl sm:text-2xl text-gray-700 italic mb-6">
              "For the first time, my son actually asks to practice. He doesn't even realize he's 
              working on speech—he just thinks he's playing a game with Pip the cat."
            </blockquote>
            <div className="font-bold text-gray-900">— Sarah M.</div>
            <div className="text-gray-500">Parent of a 5-year-old</div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-600 to-primary-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Try a Different Approach?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Start free. No credit card required. See if your child loves it.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register" 
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              Get Started Free
            </Link>
            <Link 
              to="/pricing" 
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white rounded-xl font-semibold text-lg border-2 border-white/50 hover:bg-white/10 transition-colors"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
